/**
 * CategoryThreads -- /forums/c/:slug
 *
 * Threads list scoped to a single child category. The slug is resolved
 * via `GET /api/categories?tree=true` (one call, hides archived), then
 * threads under that category are fetched via the existing
 * `GET /api/threads?category_id=...` endpoint.
 */
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, MessageCircle, PenSquare, Pin } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";

interface ApiCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  parent_id: string | null;
  display_order: number;
  children?: ApiCategory[];
}

interface ApiThread {
  id: string;
  title: string;
  body: string;
  category_id: string;
  author_id: string;
  tags: string[];
  views: number;
  reply_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  featured_until?: string | null;
  last_activity_at: string;
}

function findInTree(tree: ApiCategory[], slug: string):
  | { node: ApiCategory; parent: ApiCategory | null }
  | null {
  for (const top of tree) {
    if (top.slug === slug) return { node: top, parent: null };
    for (const child of top.children ?? []) {
      if (child.slug === slug) return { node: child, parent: top };
    }
  }
  return null;
}

export default function CategoryThreads() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = React.useState<ApiCategory | null>(null);
  const [parent, setParent] = React.useState<ApiCategory | null>(null);
  const [threads, setThreads] = React.useState<ApiThread[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const catsR = await api.get<ApiCategory[]>("/categories", {
          params: { tree: true },
        });
        if (cancelled) return;
        const hit = findInTree(catsR.data, slug);
        if (!hit) {
          setNotFound(true);
          return;
        }
        setCategory(hit.node);
        setParent(hit.parent);
        const tR = await api.get<ApiThread[]>("/threads", {
          params: { category_id: hit.node.id, limit: 50 },
        });
        if (cancelled) return;
        setThreads(tR.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }
  if (notFound || !category) {
    return (
      <div
        className="mx-auto max-w-xl px-4 py-20 text-center"
        data-testid="category-not-found"
      >
        <h1 className="text-3xl font-black mb-2">Subforum not found</h1>
        <p className="text-ink-muted mb-6">
          It may have been renamed or archived.
        </p>
        <button
          onClick={() => navigate("/forums")}
          className="text-brand font-semibold hover:text-brand-hover"
        >
          ← Back to the forum index
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-8 md:py-12" data-testid="category-threads">
      <Link
        to="/forums"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-ink mb-4"
        data-testid="category-threads-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to all categories
      </Link>

      <header className="mb-6">
        <p className="text-ink-muted text-sm uppercase tracking-wider mb-1">
          {parent ? parent.name : "Top-level category"}
        </p>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
          {category.name}
        </h1>
        {category.description && (
          <p className="text-ink-muted text-base md:text-lg max-w-2xl">
            {category.description}
          </p>
        )}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-ink-muted">
            {threads.length} thread{threads.length === 1 ? "" : "s"}
          </span>
          {user ? (
            <Link to="/forums/new" data-testid="category-new-thread">
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<PenSquare className="h-4 w-4" />}
              >
                Start a thread
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="outline" size="sm">Sign in to post</Button>
            </Link>
          )}
        </div>
      </header>

      {threads.length === 0 ? (
        <div
          data-testid="category-threads-empty"
          className="rounded-2xl border-2 border-dashed border-surface-border bg-white p-10 text-center"
        >
          <p className="text-ink font-semibold mb-1">No threads in this subforum yet.</p>
          <p className="text-ink-muted text-sm">
            Be the first to ask a question or share a repair.
          </p>
        </div>
      ) : (
        <div
          data-testid="category-thread-list"
          className="rounded-2xl border border-surface-border bg-white divide-y divide-surface-border overflow-hidden shadow-card"
        >
          {threads.map((t) => {
            const featured =
              t.featured_until && new Date(t.featured_until) > new Date();
            return (
              <Link
                key={t.id}
                to={`/forums/thread/${t.id}`}
                data-testid={`thread-link-${t.id}`}
                className="group block"
              >
                <article className="flex items-center gap-4 px-5 py-4 hover:bg-surface-sunken transition-colors min-h-12 btn-target">
                  <Avatar name={t.author_id} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.is_pinned && (
                        <Pin className="h-4 w-4 text-brand shrink-0" aria-label="Pinned" />
                      )}
                      <h3 className="font-bold text-ink text-base md:text-lg truncate group-hover:text-brand transition-colors">
                        {t.title}
                      </h3>
                      {featured && <Badge variant="brand" size="sm">Featured</Badge>}
                      {t.is_locked && <Badge variant="muted" size="sm">Locked</Badge>}
                    </div>
                    <p className="text-ink-muted text-sm line-clamp-1 mt-0.5">
                      {t.body}
                    </p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end text-xs text-ink-muted gap-1 pl-3">
                    <span className="inline-flex items-center gap-1 font-semibold text-ink">
                      <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                      {t.reply_count}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" aria-hidden />
                      {t.views}
                    </span>
                    <span className="whitespace-nowrap">
                      {relativeTime(t.last_activity_at)}
                    </span>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function relativeTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}
